import React, { useContext, useEffect, useState } from "react";
import "./ObjectivesAndRealized.scss";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { Table } from "core/table/Table";
import moment from "moment";
import {
  MONTHS,
  POPUP_STATUS,
  QUERY_STATUS,
  SCOPES,
  TAG_TYPES,
} from "constants/constants";
import Icon from "core/icon/Icon";
import Button from "core/buttons/button/Button";
import ObjectivesAndRealizedModal from "components/modal/ObjectivesAndRealizedModal";
import InfoContext from "pages/InfoContext";
import { Controller } from "utils/Controller";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import {
  OBJECTIVES_AND_REALIZED_FORM_LABELS,
  FORM_INFO,
} from "constants/forms";
import { formatNames, renderInputManager } from "components/form/CommonInputs";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { useObjectivesContext } from "api/datatrack/objectives/ObjectivesProvider";
import { extractTeamNames, formatObj } from "utils/objectivesHelper";
import LoaderV2 from "core/loader/LoaderV2";
import TableDateNav from "components/table-date-nav/TableDateNav";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const ObjectivesAndRealized = () => {
  const {
    setCode,
    setStatus,
    branchList,
    control,
    errors,
    handleSubmit,
    register,
    setValue,
    watch,
    getOptions,
    personsByProfileList,
    userScope,
    lang,
    personById,
    refresh,
    setRefresh,
    setObjReaSelectedYear,
    objReaSelectedYear,
    objReaSelectedDate,
    setObjReaSelectedDate,
    getObjectivesAndRealizedExportFile,
    loadObjectivesAndRealizedExportStatus,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const currentMonth = moment().format("MM");
  const currentMonthName = new Date(currentMonth)
    .toLocaleString("en-US", { month: "long" })
    .toLowerCase();
  const manager_id = watch("manager_id", []);
  const subsidiary = watch("branch_id", []);
  const [modal, setModal] = useState(false);
  const { getObjectivesRealized, objectivesRealized } = useObjectivesContext();
  const [formattedData, setFormattedData] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const openObjectivesAndRealizedModal = () => setModal(true);
  const closeModal = () => setModal(false);

  const header = MONTHS.map((month, i) => ({
    label: {
      key: month,
      value: (i + 1).toString().padStart(2, "0") + "/" + objReaSelectedYear,
    },
    id: month,
    fontWeight:
      (i + 1).toString().padStart(2, "0") === currentMonth ? "bold" : "normal",
  }));

  const tableLeftPart = () => {
    const isMacroScope = userScope === SCOPES.MACRO;
    const objectiveLabel = isMacroScope
      ? "team_objective"
      : "personal_objective";
    const realizedLabel = isMacroScope ? "realized_team" : "personal_realized";

    return (
      <div className="table-left-part">
        <TableDateNav
          selectedDate={objReaSelectedDate}
          setSelectedDate={setObjReaSelectedDate}
          setSelectedYear={setObjReaSelectedYear}
        />
        <div
          className={`table-left-part-sections ${
            isMacroScope ? "macro-title-left-table" : ""
          }`}
        >
          <Typography
            type="span"
            label={
              <FormattedMessage
                id={objectiveLabel}
                defaultMessage={
                  isMacroScope ? "Objectif équipe" : "Objectif personnel"
                }
              />
            }
          />
          {isMacroScope ? (
            extractTeamNames(objectivesRealized)
          ) : (
            <Typography
              type="span"
              label={
                <FormattedMessage
                  id={realizedLabel}
                  defaultMessage={
                    isMacroScope ? "Réalisé équipe" : "Réalisé personnel"
                  }
                />
              }
            />
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    getObjectivesRealized({
      userScope,
      year: objReaSelectedYear,
      branch_ids: subsidiary?.map((elem) => elem.value),
      manager_ids: manager_id?.map((elem) => elem.value),
    });
    setRefresh(false);
    setSubmitted(false);
  }, [userScope, objReaSelectedYear, refresh, submitted]);

  useEffect(() => {
    if (objectivesRealized && objectivesRealized.length > 0) {
      const cleanData = formatObj({
        objectivesRealized,
        scope: userScope,
        currentMonth: currentMonthName,
      });
      if (cleanData) {
        setFormattedData(cleanData);
      }
    }
  }, [objectivesRealized]);

  const onError = () => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };

  const onSubmit = () => {
    setSubmitted(true);
  };

  return (
    <>
      {modal && (
        <ObjectivesAndRealizedModal
          closeModal={closeModal}
          lang={lang}
          refresh={refresh}
          setRefresh={setRefresh}
        />
      )}
      <div className="dashboard-objectives-and-realized-wrapper">
        <div>
          <Typography
            style={{ marginBottom: "2rem" }}
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage
                id="objectives-and-realized"
                defaultMessage="OBJECTIFS ET RÉALISÉ"
              />
            }
          />
          {userScope === SCOPES.ADMIN && (
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <div className="section-1">
                <div
                  className={`cell-padding ${
                    manager_id?.length !== 0 ? "cell-disabled" : ""
                  }`}
                >
                  <Controller
                    {...{
                      control,
                      register,
                      name: "branch_id",
                      render: (props) => (
                        <InputSearchTag
                          readOnly={manager_id?.length !== 0}
                          errors={errors["branch_id"]}
                          label={OBJECTIVES_AND_REALIZED_FORM_LABELS.subsidiary}
                          limitChoice={5}
                          onDelete={(val) =>
                            setValue(
                              "branch_id",
                              subsidiary?.filter((elem) => elem.value !== val)
                            )
                          }
                          options={formatNames(branchList)}
                          popup={FORM_INFO.branch.popup}
                          selectedOptions={subsidiary || []}
                          setValue={(value) => {
                            setValue("branch_id", value);
                            setValue("manager_id", []);
                          }}
                          type={TAG_TYPES.BRANCHES}
                          id="branch-filter"
                          required={manager_id?.length === 0}
                          {...props}
                        />
                      ),
                    }}
                  />
                </div>
                <div
                  className={`cell-padding ${
                    subsidiary?.length !== 0 ? "cell-disabled" : ""
                  }`}
                >
                  {renderInputManager({
                    control,
                    readOnly: subsidiary?.length !== 0,
                    register,
                    personsByProfileList,
                    name: "manager_id",
                    getOptions,
                    limitChoice: 5,
                    selectedOptions: manager_id,
                    errors,
                    label: OBJECTIVES_AND_REALIZED_FORM_LABELS.manager,
                    setValue,
                    value: manager_id,
                    required: false,
                    getNamesByFilters: getNamesByFilters,
                    namesByFilters: namesByFilters,
                  })}
                </div>
              </div>
              <Button
                color="btn-medium gray-700"
                type="submit"
                id="button-search"
              >
                <FormattedMessage
                  id="search-button"
                  defaultMessage="Rechercher"
                />
              </Button>
            </form>
          )}
          <div className="CA-FAE-buttons-wrapper">
            {userScope === SCOPES.MACRO && personById?.profile_id !== "ia" && (
              <Button
                className="btn-outlined"
                size="medium"
                color="white"
                onClick={openObjectivesAndRealizedModal}
              >
                <Icon icon="icon-filled" color="light-slate-gray" height={20} />
                <FormattedMessage
                  id="team_objectives"
                  defaultMessage="Objectifs équipe"
                />
              </Button>
            )}
            <div>
              <Button
                className="btn-outlined"
                size="medium"
                color="white"
                disabled={
                  loadObjectivesAndRealizedExportStatus === QUERY_STATUS.LOADING
                }
                onClick={() =>
                  getObjectivesAndRealizedExportFile({
                    userScope,
                    queryParams: { year: Number(objReaSelectedYear) },
                    lang,
                  })
                }
              >
                <Icon icon="sheet" color="green" height={20} />
                <FormattedMessage
                  id="export_excel"
                  defaultMessage="Exporter en Excel"
                />
              </Button>
            </div>
          </div>
        </div>
        <div className="objectives-and-realized-table">
          <div style={{ display: "flex" }}>
            {!objectivesRealized?.length > 0 ? (
              <div
                style={{
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <LoaderV2 />
              </div>
            ) : (
              <>
                {tableLeftPart()}
                <Table
                  id="dashboard-objectives-and-realized-table"
                  header={header}
                  content={formattedData}
                  userScope={userScope}
                  objReaSelectedYear={objReaSelectedYear}
                  isDashboard={true}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ObjectivesAndRealized;
